import { reactive, watch, computed } from "vue";
import { createGlobalState } from "@vueuse/core";
import { i18n } from "@/i18n";
import { SIDENAV_GROUP_BY } from "@/constants";
import { UUID_SEGMENT_REGEX } from "@/constants/regex";
import { NAV_ITEMS_MODE } from "@/constants";
import { useAuthenticationStore } from "@/store/authentication.store";
import { currentOrgUuid } from "@/utils/navigation/orgUrl";
import { useFlag } from "@/composables/useFlags/useFlag";

const scopeId = (id, orgType) => {
  if (!orgType || !id || !id.startsWith(`/${orgType}`)) return id;

  const segments = id.split("/").filter(Boolean);
  const orgUuid = currentOrgUuid(orgType);
  if (!orgUuid) return id;

  // already scoped (/orgType/:uuid/...)
  if (segments[1] && UUID_SEGMENT_REGEX.test(segments[1])) {
    if (segments[1] === orgUuid) return id;
    const rest = segments.slice(2).join("/");
    return `/${orgType}/${orgUuid}${rest ? `/${rest}` : ""}`;
  }

  const suffix = id.slice(orgType.length + 1); // remove leading "/orgType"
  return `/${orgType}/${orgUuid}${suffix}`;
};

const scopeItems = (items, orgType) =>
  items.map((item) => {
    const scopedChildren = item.children
      ? scopeItems(item.children, orgType)
      : undefined;
    const scopedId = scopeId(item.id, orgType);
    return {
      ...item,
      baseId: item.baseId || item.id,
      ...(scopedId ? { id: scopedId } : {}),
      ...(scopedChildren ? { children: scopedChildren } : {}),
    };
  });

const orgTypeForMode = (mode) => {
  if (mode === NAV_ITEMS_MODE.MSP) return "msp";
  if (mode === NAV_ITEMS_MODE.SYSTEM) return "system";
  if (mode === NAV_ITEMS_MODE.CUSTOMER_ADMIN) return "customer";
  return null;
};

const SYSTEM_LINKS = [
  {
    order: 1,
    label: i18n.global.t("dashboard"),
    icon: "cb-icon-dashboard",
    id: "/system/dashboard",
    dataTestId: "system-dashboard",
    groupBy: SIDENAV_GROUP_BY.ALPHA,
  },
  {
    order: 2,
    label: i18n.global.t("step-settings"),
    icon: "cb-icon-settings",
    id: "/system/settings",
    dataTestId: "system-settings",
    groupBy: SIDENAV_GROUP_BY.BETA,
    children: [
      {
        label: i18n.global.t("SAML SSO"),
        id: "/system/settings/saml-sso",
        dataTestId: "system-settings-saml-sso",
      },
    ],
  },
];

const MSP_LINKS = [
  {
    order: 1,
    label: i18n.global.t("customers"),
    icon: "cb-icon-customers",
    exact: true,
    groupBy: SIDENAV_GROUP_BY.ALPHA,
    id: "/msp/customers",
    dataTestId: "msp-customers",
  },
  {
    order: 8,
    label: i18n.global.t("partner-portal-sidebar"),
    icon: "cb-icon-partner-portal",
    exact: true,
    groupBy: SIDENAV_GROUP_BY.NIU,
    id: "/msp/partner-portal",
    dataTestId: "partner-portal-external-link",
    isExternal: true,
    externalLink: "https://www.titanhq.com/msp-partner-portal/",
    tooltip: {
      content: i18n.global.t("partner-portal-tooltip"),
      placement: "right",
    },
  },
];

const CUSTOMER_LINKS = [];

const PROFILE_LINKS = [
  {
    order: 1,
    label: i18n.global.t("Security"),
    icon: "info-circle",
    exact: false,
    id: "/profile/security",
    dataTestId: "profile-security",
  },
];

const sortByOrder = (a, b) => a.order - b.order;

const unScopeItems = (items) =>
  items.map((item) => {
    const children = item.children ? unScopeItems(item.children) : undefined;
    return {
      ...item,
      id: item.baseId || item.id,
      ...(children ? { children } : {}),
    };
  });

export const usePrimaryNavItems = createGlobalState(() => {
  const navItemsState = new Map();

  const rescopeSystem = (existing) =>
    scopeItems(existing || [...SYSTEM_LINKS], "system").sort(sortByOrder);
  const rescopeMsp = (existing) =>
    scopeItems(existing || [...MSP_LINKS], "msp").sort(sortByOrder);

  navItemsState.set(NAV_ITEMS_MODE.SYSTEM, rescopeSystem());
  navItemsState.set(NAV_ITEMS_MODE.MSP, rescopeMsp());
  navItemsState.set(
    NAV_ITEMS_MODE.CUSTOMER_ADMIN,
    [...CUSTOMER_LINKS].sort(sortByOrder),
  );
  navItemsState.set(
    NAV_ITEMS_MODE.PROFILE,
    [...PROFILE_LINKS].sort(sortByOrder),
  );

  const navItems = reactive(navItemsState);

  // Keep system links scoped to the current org (root or child)
  const auth = useAuthenticationStore();
  const rescopeSystemLinks = () =>
    navItems.set(
      NAV_ITEMS_MODE.SYSTEM,
      rescopeSystem(navItems.get(NAV_ITEMS_MODE.SYSTEM)),
    );
  const rescopeMspLinks = () =>
    navItems.set(
      NAV_ITEMS_MODE.MSP,
      rescopeMsp(navItems.get(NAV_ITEMS_MODE.MSP)),
    );
  const rescopeCustomerLinks = () => {
    const existing = navItems.get(NAV_ITEMS_MODE.CUSTOMER_ADMIN);
    const baseItems = existing ? unScopeItems(existing) : [...CUSTOMER_LINKS];
    navItems.set(
      NAV_ITEMS_MODE.CUSTOMER_ADMIN,
      scopeItems(baseItems, "customer").sort(sortByOrder),
    );
  };
  watch(
    () => auth.selectedMsp,
    () => {
      rescopeSystemLinks();
      rescopeMspLinks();
    },
    { deep: false },
  );
  watch(
    () => auth.selectedAccount?.uuid,
    () => {
      rescopeSystemLinks();
      rescopeCustomerLinks();
    },
    { deep: false },
  );
  watch(
    () => auth.selectedCustomer,
    () => {
      rescopeCustomerLinks();
    },
    { deep: false },
  );

  const registerNavItems = (type, items) => {
    if (!navItems.has(type)) {
      throw new Error(`No links for type: '${type}'`);
    }

    const orgType = orgTypeForMode(type);
    const scoped = scopeItems(items, orgType);
    const prevLinks = navItems.get(type);
    navItems.set(type, [...prevLinks, ...scoped].sort(sortByOrder));
  };

  // Feature-flag gating for nav items happens here — in a reactive context —
  // rather than at module-load time in the item constants. Reading a flag
  // before the Harness SDK is ready falls back to the static matrix value; by
  // resolving flags inside a computed we pick up the real Harness value the
  // moment it arrives, so gated items appear without a reload.
  // See docs: nav items may carry a `flag` (FlagName; shown only while ON) or
  // a `hideFlag` (FlagName; hidden while ON); absent = always shown.
  const flagRefs = new Map();
  const isFlagOn = (flagName) => {
    if (!flagRefs.has(flagName)) {
      flagRefs.set(flagName, useFlag(flagName).isFeatureAvailable);
    }
    return flagRefs.get(flagName).value;
  };

  const filterByFlags = (items) =>
    (items || [])
      .filter((item) => !item.flag || isFlagOn(item.flag))
      .filter((item) => !item.hideFlag || !isFlagOn(item.hideFlag))
      .map((item) =>
        item.children
          ? { ...item, children: filterByFlags(item.children) }
          : item,
      );

  const getNavItems = (type) =>
    computed(() => filterByFlags(navItems.get(type)));

  return { navItems, registerNavItems, getNavItems };
});

export default {};
